import { Inject, Injectable } from "@angular/core";
import {
    ANGULARJS_ROOTSCOPE,
    MONITORING_SERVICE,
    NMS_STATES,
    STATE,
    ZABBIX_MONITORING_SERVICE
} from "@nms-ng2/app/shared/services/upgraded-provider/upgraded-providers";
import { of } from "rxjs";

import { DeviceMonitoringService } from "./device-monitoring.service";
import { Link, MessageConfig, WarningSettings } from "../../components/elements/nms-warning/nms-warning.models";

interface ZabbixWarning {
    show: boolean;
    internalLink: Link;
    externalLink: Link;
    generalMessage: MessageConfig;
    needsAdministratorPermission: boolean;
}

interface ZabbixWarningSettings {
    show: boolean;
    header: string;
    warnings: Array<ZabbixWarning>;
}

interface MonitoringConnectionSettings {
    url: string;
}

/**
 * Serviço responsável por realizar as requisições para monitorar o status do equipamento.
 */
@Injectable({
    providedIn: "root"
})
export class ZabbixWarningService {

    constructor(private readonly deviceMonitoringService: DeviceMonitoringService,
                @Inject(MONITORING_SERVICE) private readonly monitoringService: any,
                @Inject(ANGULARJS_ROOTSCOPE) private readonly $rootScope,
                @Inject(ZABBIX_MONITORING_SERVICE) private readonly zabbixMonitoringService: any,
                @Inject(STATE) private readonly state: any,
                @Inject(NMS_STATES) private readonly nmsStates: any) {
    }

    async getZabbixWarnings(resourceId: number): Promise<WarningSettings> {
        if (!resourceId) {
            return Promise.resolve(null);
        }

        const monitoringSettings: MonitoringConnectionSettings = await this.monitoringService.getMonitoringSettings();

        const notMonitoredConfiguration = "NOT_MONITORED_DUE_MISCONFIGURATION";
        let monitoringStatusObservable = of(notMonitoredConfiguration);

        if (monitoringSettings.url) {
            monitoringStatusObservable = this.deviceMonitoringService.getMonitoringStatus(resourceId);
        }

        const monitoringStatus = await monitoringStatusObservable.toPromise();

        if (!monitoringSettings.url || monitoringStatus !== notMonitoredConfiguration) {
            const zabbixWarningSettings: ZabbixWarningSettings = this.zabbixMonitoringService.getSettings(
                monitoringSettings.url,
                monitoringStatus
            );
            zabbixWarningSettings.warnings = this.filterWarningsByAdminPermissions(zabbixWarningSettings.warnings);
            return Promise.resolve(this.convertToWarningSettings(zabbixWarningSettings));
        }

        return Promise.resolve(null);
    }

    private filterWarningsByAdminPermissions(warnings: ZabbixWarning[]): any[] {
        return warnings.filter((warning) => {
            if (warning.needsAdministratorPermission === undefined) {
                warning.needsAdministratorPermission = true;
            }

            return this.$rootScope.isAdmin === warning.needsAdministratorPermission;
        });
    }

    private convertToWarningSettings(warningSettings: ZabbixWarningSettings): WarningSettings {
        return {
            header: warningSettings.header,
            show: warningSettings.show,
            warnings: warningSettings.warnings.map((warning) => {
                let internalLink;

                if (warning.internalLink) {
                    internalLink = {
                        translateKey: warning.internalLink?.translateKey,
                        action: () => this.state.go(this.nmsStates.management.monitoring, {})
                    };
                }

                return {
                    externalLink: warning.externalLink,
                    generalMessage: warning.generalMessage,
                    internalLink,
                    show: warning.show
                };
            })
        };
    }
}
